import React, { useState, useEffect } from 'react';

const AIRevolutionCTA = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('ai-revolution-cta');
    if (element) observer.observe(element);

    return () => {
      window.removeEventListener('resize', checkMobile);
      if (element) observer.unobserve(element);
    };
  }, []);

  const styles = {
    container: {
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      padding: isMobile ? '4rem 1rem' : '6rem 2rem',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden'
    },
    backgroundPattern: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        radial-gradient(circle at 20% 20%, rgba(255,255,255,0.1) 0%, transparent 50%),
        radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%),
        radial-gradient(circle at 40% 60%, rgba(255,255,255,0.05) 0%, transparent 50%)
      `,
      pointerEvents: 'none'
    },
    content: {
      position: 'relative',
      zIndex: 2,
      maxWidth: '800px',
      margin: '0 auto'
    },
    title: {
      fontSize: isMobile ? '2.25rem' : '3rem',
      fontWeight: 'bold',
      color: 'white',
      marginBottom: '1rem',
      lineHeight: '1.2',
      transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
      opacity: isVisible ? 1 : 0,
      transition: 'all 0.8s ease'
    },
    subtitle: {
      fontSize: isMobile ? '1.125rem' : '1.25rem',
      color: 'rgba(255, 255, 255, 0.9)',
      marginBottom: '1rem',
      fontWeight: '500',
      transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
      opacity: isVisible ? 1 : 0,
      transition: 'all 0.8s ease 0.2s'
    },
    description: {
      fontSize: isMobile ? '1rem' : '1.125rem',
      color: 'rgba(255, 255, 255, 0.8)',
      marginBottom: '2.5rem',
      lineHeight: '1.6',
      maxWidth: '600px',
      margin: '0 auto 2.5rem auto',
      transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
      opacity: isVisible ? 1 : 0,
      transition: 'all 0.8s ease 0.4s'
    },
    buttonContainer: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: '1rem',
      justifyContent: 'center',
      alignItems: 'center',
      transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
      opacity: isVisible ? 1 : 0,
      transition: 'all 0.8s ease 0.6s'
    },
    primaryButton: {
      backgroundColor: 'white',
      color: '#059669',
      padding: '1rem 2rem',
      borderRadius: '8px',
      border: 'none',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      width: isMobile ? '100%' : 'auto',
      maxWidth: isMobile ? '300px' : 'none'
    },
    secondaryButton: {
      backgroundColor: 'transparent',
      color: 'white',
      padding: '1rem 2rem',
      borderRadius: '8px',
      border: '2px solid rgba(255, 255, 255, 0.8)',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      width: isMobile ? '100%' : 'auto',
      maxWidth: isMobile ? '300px' : 'none'
    },
    tertiaryButton: {
      backgroundColor: 'transparent',
      color: 'white',
      padding: '1rem 2rem',
      borderRadius: '8px',
      border: '2px solid rgba(255, 255, 255, 0.6)',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      width: isMobile ? '100%' : 'auto',
      maxWidth: isMobile ? '300px' : 'none'
    },
    decorativeElements: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: 'none'
    },
    floatingCircle: {
      position: 'absolute',
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.1)',
      animation: 'float 6s ease-in-out infinite'
    }
  };

  return (
    <div id="ai-revolution-cta" style={styles.container}>
      <div style={styles.backgroundPattern}></div>
      
      {/* Decorative floating elements */}
      <div style={styles.decorativeElements}>
        <div style={{
          ...styles.floatingCircle,
          width: '100px',
          height: '100px',
          top: '10%',
          left: '10%',
          animationDelay: '0s'
        }}></div>
        <div style={{
          ...styles.floatingCircle,
          width: '60px',
          height: '60px',
          top: '20%',
          right: '15%',
          animationDelay: '2s'
        }}></div>
        <div style={{
          ...styles.floatingCircle,
          width: '80px',
          height: '80px',
          bottom: '15%',
          left: '20%',
          animationDelay: '4s'
        }}></div>
        <div style={{
          ...styles.floatingCircle,
          width: '40px',
          height: '40px',
          bottom: '25%',
          right: '10%',
          animationDelay: '1s'
        }}></div>
      </div>

      <div style={styles.content}>
        <h2 style={styles.title}>
          Ready to Join the AI Revolution?
        </h2>
        
        <p style={styles.subtitle}>
          Choose your path to AI excellence with Global AI Society.
        </p>
        
        <p style={styles.description}>
          From individual learning to institutional partnerships, we have the right program for your AI education journey.
        </p>
        
        <div style={styles.buttonContainer}>
          <a href='/student-membership' style={{ textDecoration: 'none' }}>
          <button 
            style={styles.primaryButton}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.2)';
              e.target.style.backgroundColor = '#f8fafc';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              e.target.style.backgroundColor = 'white';
            }}
          >
            Join as Student
          </button>
          </a>
          
          <a href="/become-partner" style={{ textDecoration: 'none' }}>
          <button 
            style={styles.secondaryButton}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.borderColor = 'white';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'translateY(0)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.8)';
            }}
          >
            Partner with Us
          </button>
          </a>
          
          <a href='/programs' style={{ textDecoration: 'none' }}>
          <button 
            style={styles.tertiaryButton}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.9)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'translateY(0)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.6)';
            }}
          >
            Explore Programs
          </button>
          </a>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
      `}</style>
    </div>
  );
};

export default AIRevolutionCTA;